import moment, { Moment } from 'moment';
import { Employee } from 'domain/Employee';
import { Shift } from 'domain/Shift';
import { Holiday } from 'domain/Holiday';
import { EmployeeAvailability } from 'domain/EmployeeAvailability';

export interface AvailabilityCardResult {
  showCardBefore: boolean;
  showCardAfter: boolean;
  showUnavailableCardBefore: boolean;
  showUnavailableCardAfter: boolean;
  unavailableReason: string | null;
  unavailableStart: string | null;
  unavailableEnd: string | null;
  unavailableAllDay: boolean;
}

const emptyResult = (): AvailabilityCardResult => ({
  showCardBefore: false,
  showCardAfter: false,
  showUnavailableCardBefore: false,
  showUnavailableCardAfter: false,
  unavailableReason: null,
  unavailableStart: null,
  unavailableEnd: null,
  unavailableAllDay: false,
});

/**
 * Determina si dos intervalos tienen algún tiempo en común.
 *
 * Los límites se manejan de forma exclusiva:
 *
 * 08:00 - 12:00
 * 12:00 - 14:00
 *
 * No se consideran superpuestos.
 */
const intervalsOverlap = (
  firstStart: Moment,
  firstEnd: Moment,
  secondStart: Moment,
  secondEnd: Moment
): boolean =>
  firstStart.isBefore(secondEnd) &&
  firstEnd.isAfter(secondStart);

export function calculateAvailabilityCard(
  employee: Employee | null,
  day: Moment,
  employeeShifts: Shift[],
  holidays: Holiday[],
  employeeAvailabilities: EmployeeAvailability[]
): AvailabilityCardResult {
  if (!employee) {
    return emptyResult();
  }

  const dayStart = day.clone().startOf('day');
  const dayEnd = day.clone().endOf('day');
  const noon = day.clone().startOf('day').hour(12);

  const isSunday = day.day() === 0;
  const isSaturday = day.day() === 6;

  const isHoliday = holidays.some((holiday) =>
    moment(holiday.date).isSame(day, 'day')
  );

  /*
   * ============================================================
   * TURNOS DEL DÍA
   * ============================================================
   */

  const shiftsForDay = employeeShifts
    .filter((shift) =>
      moment(shift.startDateTime).isSame(day, 'day')
    )
    .sort((firstShift, secondShift) =>
      moment(firstShift.startDateTime).diff(
        moment(secondShift.startDateTime)
      )
    );

  const workedMinutesDay = shiftsForDay.reduce(
    (total, shift) =>
      total +
      moment(shift.endDateTime).diff(
        moment(shift.startDateTime),
        'minutes'
      ),
    0
  );

  /*
   * ============================================================
   * MINUTOS TRABAJADOS EN LA SEMANA
   * ============================================================
   */

  const weekStart = day.clone().startOf('isoWeek');
  const weekEnd = day.clone().endOf('isoWeek');

  const workedMinutesWeek = employeeShifts
    .filter((shift) => {
      const shiftStart = moment(shift.startDateTime);

      return shiftStart.isBetween(
        weekStart,
        weekEnd,
        undefined,
        '[]'
      );
    })
    .reduce(
      (total, shift) =>
        total +
        moment(shift.endDateTime).diff(
          moment(shift.startDateTime),
          'minutes'
        ),
      0
    );

  const maximumMinutesPerDay =
    employee.contract?.maximumMinutesPerDay;

  const maximumMinutesPerWeek =
    employee.contract?.maximumMinutesPerWeek;

  const hasContractAvailability =
    (
      maximumMinutesPerDay == null ||
      workedMinutesDay < maximumMinutesPerDay
    ) &&
    (
      maximumMinutesPerWeek == null ||
      workedMinutesWeek < maximumMinutesPerWeek
    );

  /*
   * ============================================================
   * INDISPONIBILIDAD DEL DÍA
   * ============================================================
   *
   * Se toma la indisponibilidad que tiene intersección con el día
   * que se está renderizando.
   */

  const unavailableEntry = employeeAvailabilities.find(
    (availability) => {
      if (availability.state !== 'UNAVAILABLE') {
        return false;
      }

      const availabilityStart = moment(
        availability.startDateTime
      );

      const availabilityEnd = moment(
        availability.endDateTime
      );

      return intervalsOverlap(
        availabilityStart,
        availabilityEnd,
        dayStart,
        dayEnd
      );
    }
  );

  let visibleUnavailableStart: Moment | null = null;
  let visibleUnavailableEnd: Moment | null = null;

  let unavailableStart: string | null = null;
  let unavailableEnd: string | null = null;
  let unavailableAllDay = false;

  if (unavailableEntry) {
    const originalStart = moment(
      unavailableEntry.startDateTime
    );

    const originalEnd = moment(
      unavailableEntry.endDateTime
    );

    /*
     * Cuando una indisponibilidad cruza varios días,
     * mostramos solamente la parte correspondiente al día actual.
     */
    visibleUnavailableStart = moment.max(
      originalStart,
      dayStart
    );

    visibleUnavailableEnd = moment.min(
      originalEnd,
      dayEnd
    );

    unavailableAllDay =
      visibleUnavailableStart.isSame(dayStart) &&
      visibleUnavailableEnd.isSame(dayEnd);

    if (!unavailableAllDay) {
      unavailableStart =
        visibleUnavailableStart.format('hh:mm A');

      unavailableEnd =
        visibleUnavailableEnd.format('hh:mm A');
    }
  }

  /*
   * ============================================================
   * RESULTADO
   * ============================================================
   */

  let showCardBefore = false;
  let showCardAfter = false;

  let showUnavailableCardBefore = false;
  let showUnavailableCardAfter = false;

  const hasShifts = shiftsForDay.length > 0;

  /*
   * La tarjeta verde:
   *
   * - Nunca aparece domingos.
   * - Nunca aparece festivos.
   * - El sábado solamente aparece cuando no existen turnos.
   * - Se oculta cuando el empleado alcanzó sus límites de contrato.
   */
  const greenCardAllowed =
    hasContractAvailability &&
    !isSunday &&
    !isHoliday &&
    (!isSaturday || !hasShifts);

  /*
   * ============================================================
   * CASO 1: NO HAY TURNOS
   * ============================================================
   */

  if (!hasShifts) {
    /*
     * Sin indisponibilidad:
     *
     *   [VERDE]
     */
    if (!unavailableEntry) {
      return {
        ...emptyResult(),
        showCardBefore: greenCardAllowed,
      };
    }

    /*
     * Indisponibilidad durante todo el día:
     *
     *   [ROJA TODO EL DÍA]
     */
    if (
      unavailableAllDay ||
      !visibleUnavailableStart ||
      !visibleUnavailableEnd
    ) {
      return {
        ...emptyResult(),
        showUnavailableCardBefore: true,
        unavailableReason:
          unavailableEntry.reason ?? null,
        unavailableStart,
        unavailableEnd,
        unavailableAllDay,
      };
    }

    const unavailableStartsAtDayStart =
      visibleUnavailableStart.isSame(dayStart);

    const unavailableEndsAtDayEnd =
      visibleUnavailableEnd.isSame(dayEnd);

    /*
     * Indisponibilidad desde las 00:00 hasta una hora determinada:
     *
     *   [ROJA]
     *   [VERDE]
     *
     * Ejemplo:
     *   00:00 - 12:30 indisponible
     *   12:30 - final del día disponible
     */
    if (
      unavailableStartsAtDayStart &&
      !unavailableEndsAtDayEnd
    ) {
      showUnavailableCardBefore = true;
      showCardAfter = greenCardAllowed;
    }

    /*
     * Indisponibilidad desde una hora determinada hasta el
     * final del día:
     *
     *   [VERDE]
     *   [ROJA]
     *
     * Ejemplo:
     *   00:00 - 14:00 disponible
     *   14:00 - final del día indisponible
     */
    else if (
      !unavailableStartsAtDayStart &&
      unavailableEndsAtDayEnd
    ) {
      showCardBefore = greenCardAllowed;
      showUnavailableCardAfter = true;
    }

    /*
     * Indisponibilidad ubicada en una parte intermedia del día.
     *
     * Debido a que la interfaz solamente permite una posición
     * "antes" y otra posición "después", ubicamos la tarjeta roja
     * según la mitad del día.
     */
    else {
      const unavailableMidpoint = visibleUnavailableStart
        .clone()
        .add(
          visibleUnavailableEnd.diff(
            visibleUnavailableStart,
            'minutes'
          ) / 2,
          'minutes'
        );

      if (unavailableMidpoint.isBefore(noon)) {
        showUnavailableCardBefore = true;
        showCardAfter = greenCardAllowed;
      } else {
        showCardBefore = greenCardAllowed;
        showUnavailableCardAfter = true;
      }
    }

    return {
      showCardBefore,
      showCardAfter,
      showUnavailableCardBefore,
      showUnavailableCardAfter,
      unavailableReason:
        unavailableEntry.reason ?? null,
      unavailableStart,
      unavailableEnd,
      unavailableAllDay,
    };
  }

  /*
   * ============================================================
   * CASO 2: EXISTEN TURNOS
   * ============================================================
   */

  const firstShiftStart = moment(
    shiftsForDay[0].startDateTime
  );

  const lastShiftEnd = moment(
    shiftsForDay[shiftsForDay.length - 1].endDateTime
  );

  /*
   * Existen dos espacios posibles para las tarjetas:
   *
   * 1. Antes del primer turno.
   * 2. Después del último turno.
   */
  const beforeZoneStart = dayStart;
  const beforeZoneEnd = firstShiftStart;

  const afterZoneStart = lastShiftEnd;
  const afterZoneEnd = dayEnd;

  const hasTimeBeforeFirstShift =
    beforeZoneEnd.isAfter(beforeZoneStart);

  const hasTimeAfterLastShift =
    afterZoneEnd.isAfter(afterZoneStart);

  /*
   * ============================================================
   * TARJETA ROJA
   * ============================================================
   *
   * La tarjeta roja aparece siempre, incluso:
   *
   * - sábados;
   * - domingos;
   * - festivos;
   * - cuando el empleado llegó al máximo contractual.
   */

  if (
    unavailableEntry &&
    visibleUnavailableStart &&
    visibleUnavailableEnd
  ) {
    const unavailableBeforeShift =
      hasTimeBeforeFirstShift &&
      intervalsOverlap(
        visibleUnavailableStart,
        visibleUnavailableEnd,
        beforeZoneStart,
        beforeZoneEnd
      );

    const unavailableAfterShift =
      hasTimeAfterLastShift &&
      intervalsOverlap(
        visibleUnavailableStart,
        visibleUnavailableEnd,
        afterZoneStart,
        afterZoneEnd
      );

    if (unavailableBeforeShift) {
      showUnavailableCardBefore = true;
    }

    if (unavailableAfterShift) {
      showUnavailableCardAfter = true;
    }

    /*
     * Si la indisponibilidad queda completamente dentro del rango
     * comprendido por los turnos, no coincide con las zonas externas.
     *
     * Como la tarjeta roja debe aparecer siempre, se coloca en el
     * lado más coherente según su posición en el día.
     */
    if (
      !showUnavailableCardBefore &&
      !showUnavailableCardAfter
    ) {
      const unavailableMidpoint = visibleUnavailableStart
        .clone()
        .add(
          visibleUnavailableEnd.diff(
            visibleUnavailableStart,
            'minutes'
          ) / 2,
          'minutes'
        );

      const shiftsMidpoint = firstShiftStart
        .clone()
        .add(
          lastShiftEnd.diff(
            firstShiftStart,
            'minutes'
          ) / 2,
          'minutes'
        );

      if (unavailableMidpoint.isBefore(shiftsMidpoint)) {
        showUnavailableCardBefore = true;
      } else {
        showUnavailableCardAfter = true;
      }
    }
  }

  /*
   * ============================================================
   * TARJETA VERDE
   * ============================================================
   *
   * En días laborales:
   *
   * - Si todos los turnos están en la tarde, aparece antes.
   * - Si todos los turnos están en la mañana, aparece después.
   *
   * No se muestra una tarjeta verde en la misma posición donde ya
   * existe una tarjeta roja.
   */

  if (greenCardAllowed) {
    /*
     * Tarjeta verde antes del turno:
     *
     * Se muestra cuando el primer turno comienza al mediodía
     * o después.
     *
     * Resultado:
     *
     *   [VERDE]
     *   [TURNO]
     */
    const shiftLeavesMorningAvailable =
      firstShiftStart.isSameOrAfter(noon);

    if (
      shiftLeavesMorningAvailable &&
      hasTimeBeforeFirstShift &&
      !showUnavailableCardBefore
    ) {
      showCardBefore = true;
    }

    /*
     * Tarjeta verde después del turno:
     *
     * Se muestra cuando el último turno termina al mediodía
     * o antes.
     *
     * Resultado:
     *
     *   [TURNO]
     *   [VERDE]
     */
    const shiftLeavesAfternoonAvailable =
      lastShiftEnd.isSameOrBefore(noon);

    if (
      shiftLeavesAfternoonAvailable &&
      hasTimeAfterLastShift &&
      !showUnavailableCardAfter
    ) {
      showCardAfter = true;
    }
  }

  return {
    showCardBefore,
    showCardAfter,
    showUnavailableCardBefore,
    showUnavailableCardAfter,
    unavailableReason:
      unavailableEntry?.reason ?? null,
    unavailableStart,
    unavailableEnd,
    unavailableAllDay,
  };
}